/* eslint-disable camelcase */
import { Fragment, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { results, PlainTable } from '@mdc/ui';
import { useTranslation } from 'react-i18next';
import NETWORK_DATA from './OpswatNetworkData';
import ReactDOMServer from 'react-dom/server';
import { debounce } from 'lodash';
import { CDN_PATH } from '@mdc/constants';

const Leaflet = typeof window !== 'undefined' ? require('leaflet') : null;
const Map = typeof window !== 'undefined' ? require('react-leaflet').Map : null;
const TileLayer = typeof window !== 'undefined' ? require('react-leaflet').TileLayer : null;
const Marker = typeof window !== 'undefined' ? require('react-leaflet').Marker : null;
const Tooltip = typeof window !== 'undefined' ? require('react-leaflet').Tooltip : null;

import 'leaflet/dist/leaflet.css';
import './OpswatSandboxNetwork.scss';

const { PlainTablePlaceholder } = results;

const NETWORK_TYPE = [
    { key: 'tcp', name: 'TCP' },
    { key: 'udp', name: 'UDP' },
    { key: 'http', name: 'HTTP' }
];

const OpswatSandboxNetwork = ({ resultsLoading, scanning, networkDetails, processRawData }) => {
    const [isDraggingAllowed, setIsDraggingAllowed] = useState(false);
    const [mapCenter, setMapCenter] = useState([20, 0]);
    const [isClient, setIsClient] = useState(false);
    const { t, ready, i18n } = useTranslation();
    const lang = i18n?.language?.split('-')[0] || 'en';

    useEffect(() => {
        if (!isClient) {
            setIsClient(true);
        }
    }, []);

    if (Leaflet) {
        Leaflet.Icon.Default.imagePath = '../node_modules/leaflet';
        delete Leaflet.Icon.Default.prototype._getIconUrl;
        Leaflet.Icon.Default.mergeOptions({
            iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
            iconUrl: require('leaflet/dist/images/marker-icon.png'),
            shadowUrl: require('leaflet/dist/images/marker-shadow.png')
        });
    }

    const markerIconHTML = ReactDOMServer.renderToString(<span className='markerIcon'/>);
    const customMarkerIcon = new Leaflet.DivIcon({
        html: markerIconHTML,
    });

    const locations = useMemo(() => {
        if (!networkDetails || !networkDetails?.geolocation) {
            return [];
        }

        const { geolocation: geoLocation } = networkDetails;

        return Object.keys(geoLocation).map((location) => ({
            ip: location,
            country: geoLocation[location]?.country,
            city: geoLocation[location]?.city,
            latitude: geoLocation[location]?.latitude,
            longitude: geoLocation[location]?.longitude,
        }));
    }, [networkDetails]);

    const tableData = useMemo(() => {
        if (networkDetails) {
            const { arp, dns, conn } = networkDetails;

            return [...arp || [], ...dns || [], ...conn || []].reduce((acc, item) => {
                if (item?.protocol && ['tcp', 'udp', 'http'].includes(item.protocol.toLowerCase())) {
                    acc[item.protocol].push(item);
                }

                return acc;
            }, { tcp: [], udp: [], http: [] });
        }

        return { tcp: [], udp: [], http: [] };
    }, [networkDetails, t]);

    const dynamicAnalysisDom = useMemo(() => {
        if (!tableData || resultsLoading || scanning) {
            return <PlainTablePlaceholder numberOfColumns={3}/>;
        }

        return NETWORK_TYPE.map((networkType, index) => {
            const networkTypeData = tableData[networkType.key];
            const networkDataWithLocation = networkTypeData?.reduce((acc, item) => {
                const dstHost = item?.dst_host;
                const isHostInGeoLocation = locations?.filter((location) => location?.ip === dstHost);

                if (isHostInGeoLocation.length) {
                    acc.push({ ...item, geoLocation: isHostInGeoLocation[0] });
                }

                return acc;
            }, []);

            const networkData = networkDataWithLocation?.length ? networkDataWithLocation : networkTypeData;

            const networkTypeProcessedData = networkData ? processRawData(networkData, NETWORK_DATA(t, lang)[networkType.key], true) : [];

            return <Fragment key={index}>
                <h6 className={index > 0 ? 'mt-5' : ''}>{networkType.name}{' '}{`(${tableData[networkType.key].length})`}</h6>
                <hr/>
                {networkTypeProcessedData.length && <PlainTable
                    key={networkType.key}
                    columnsData={NETWORK_DATA(t, lang)[networkType.key]}
                    data={networkTypeProcessedData}
                    hasLoadMore={networkTypeProcessedData?.length > 10 ? true : false}
                /> || ''}
            </Fragment>;
        });
    }, [tableData, resultsLoading, scanning, locations]);

    const onResize = debounce(() => {
        if (window.innerWidth > 768) {
            setIsDraggingAllowed(false);
            setMapCenter([20, 0]);
        } else {
            setIsDraggingAllowed(true);
        }
    }, 10);

    useEffect(() => {
        onResize();

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [isClient]);

    const handleOnMoveEnd = debounce((e) => {
        setMapCenter(e?.target?.getCenter());
    }, 10);

    const leafletMapDOM = useMemo(() => {
        if (typeof window !== 'undefined') {
            const mapTilesURL = process.env.ENVIRONMENT === 'local' ? '/resources/images/map' : `${CDN_PATH}/resources/images/map`;

            return <Map
                center={mapCenter}
                zoom={1.55}
                dragging={isDraggingAllowed}
                doubleClickZoom={false}
                scrollWheelZoom={false}
                zoomControl={false}
                useFlyTo={true}
                zoomSnap={1.55}
                trackResize={true}
                onMoveEnd={handleOnMoveEnd}
            >
                <TileLayer
                    attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a>'
                    url={`${mapTilesURL}/{z}/{x}/{y}@2x.png`}
                    noWrap={true}
                />
                {
                    locations.map((location, index) => {
                        const position = [location?.latitude ?? 0, location?.longitude ?? 0];
                        return <Marker key={index} position={position} icon={customMarkerIcon}>
                            <Tooltip direction={'top'} offset={[0, 0]} opacity={1} permanent>{`${location?.country} - ${location?.city}`}</Tooltip>
                        </Marker>;
                    })
                }
            </Map>;
        }
    }, [locations, isDraggingAllowed, mapCenter]);

    if (!ready) {
        return null;
    }

    return <Row className='opswatSandboxNetwork'>
        <Col xs={12}>
            <h6>{t('Overview')}</h6>
            <p>{t('This map shows places that the sample tried to connect to')}{'.'}</p>

            <div className='map'>
                {leafletMapDOM}
            </div>
        </Col>
        <Col xs={12} className='networkTable mt-3'>
            {dynamicAnalysisDom}
        </Col>
    </Row>;
};

OpswatSandboxNetwork.propTypes = {
    resultsLoading: PropTypes.bool,
    scanning: PropTypes.string,
    networkDetails: PropTypes.object,
    processRawData: PropTypes.func
};

export default OpswatSandboxNetwork;
